import { useState, type FormEvent } from "react";
import { ArrowUp, RotateCcw, Sparkles } from "lucide-react";
import { Chip } from "@/ui";
import { naraSuggestions } from "@/mock";
import { cn } from "@/lib/cn";
import { MAKE_MODES, MODE_PLACEHOLDER, type MakeMode } from "./makeModes";

/**
 * The prompt field: a white input with a coral focus ring, a mode selector
 * (chips) and the canonical Nara suggestion chips. Nara proposes from here.
 */
export function PromptComposer({
  mode,
  onModeChange,
  value,
  onValueChange,
  onSubmit,
  onReset,
  streaming,
  started,
}: {
  mode: MakeMode;
  onModeChange: (m: MakeMode) => void;
  value: string;
  onValueChange: (v: string) => void;
  onSubmit: (prompt: string) => void;
  onReset: () => void;
  streaming: boolean;
  started: boolean;
}) {
  const [focused, setFocused] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = value.trim();
    if (text && !streaming) {
      onSubmit(text);
      onValueChange(""); // clear the composer after sending
    }
  };

  return (
    <div className="space-y-3">
      {/* mode selector */}
      <div className="flex flex-wrap items-center gap-2">
        {MAKE_MODES.map((m) => {
          const Icon = m.icon;
          const active = m.value === mode;
          return (
            <Chip key={m.value} active={active} onClick={() => onModeChange(m.value)}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
              {m.label}
            </Chip>
          );
        })}
        {started && (
          <button
            type="button"
            onClick={onReset}
            className="ml-auto inline-flex items-center gap-1.5 rounded-chip px-2 py-1 text-[12px] text-muted transition-colors hover:text-ink"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
            Start over
          </button>
        )}
      </div>

      {/* prompt field — white, coral focus ring */}
      <form onSubmit={submit}>
        <div
          className={cn(
            "flex items-end gap-2 rounded-card bg-paper p-2.5 shadow-sm transition-all duration-200 ease-out-soft dark:shadow-none",
            focused
              ? "ring-2 ring-accent/60 dark:ring-accent/50"
              : "border border-hairline",
          )}
        >
          <Sparkles className="mb-2 ml-1 h-[18px] w-[18px] shrink-0 text-accent" strokeWidth={1.5} />
          <textarea
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) submit(e);
            }}
            rows={1}
            placeholder={MODE_PLACEHOLDER[mode]}
            className="max-h-40 min-h-[44px] w-full resize-none self-center bg-transparent py-2 text-[16px] leading-snug text-ink outline-none placeholder:text-subtle"
          />
          <button
            type="submit"
            disabled={!value.trim() || streaming}
            aria-label="Send to Nara"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-white shadow-[0_6px_18px_-8px_rgba(184,68,34,0.7)] transition-all duration-200 ease-out-soft hover:bg-accent-hover active:translate-y-px disabled:opacity-40"
          >
            <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.25} />
          </button>
        </div>
      </form>

      {/* suggestion chips */}
      {!started && (
        <div className="space-y-2">
          <p className="eyebrow">Try one of these</p>
          <div className="flex flex-wrap gap-2">
            {naraSuggestions.map((s) => (
              <Chip key={s} dashed onClick={() => onValueChange(s)}>
                {s}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
